import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { MORTGAGES_PATH } from "../../../../config/site/ratecompare";
import { BaseVerticalCheck } from "../../../components/BaseComponents/BaseVerticalCheck";
import {
	FilterProvider,
	useDesktopFilterSelect,
	useFilter,
	useMobileFilterSelect,
} from "../../../components/Filtering/FilteringProvider";
import { getFilteringControls } from "../../../selectors";
import { HousePercentageIcon } from "../../assets/HousePercentageIcon";

export const MortgagesFilters = () => (
	<BaseVerticalCheck vertical={MORTGAGES_PATH}>
		<FilterProvider>
			<MortgagesFiltersContent />
		</FilterProvider>
	</BaseVerticalCheck>
);

const MortgagesFiltersContent = () => {
	const filteringControls = useSelector(getFilteringControls);

	return (
		<>
			{/* Desktop View */}
			<div className="tw:absolute tw:top-[190px] tw:hidden tw:w-full tw:bg-white/30 tw:py-7 tw:md:top-[321px] tw:md:block tw:lg:top-[414px]">
				<div className="tw:canister tw:flex tw:flex-col tw:items-center tw:lg:flex-row">
					<HousePercentageIcon className="tw:h-[40px] tw:w-[40px] tw:md:h-[56px] tw:md:w-[56px]" />
					<p className="tw:ml-4 tw:max-w-[250px] tw:py-2 tw:text-lg tw:leading-[1.75rem] tw:font-medium tw:text-white tw:md:text-xl">
						Which Rate is Best For You?
					</p>
					<div className="tw:flex tw:h-[64px] tw:w-full">
						<div className="tw:flex tw:w-full tw:justify-between tw:gap-2 tw:md:gap-3">
							{filteringControls.map((control, idx) => {
								const { icon, title, filteringKey } = control;

								return (
									<div
										className="tw:flex tw:w-1/3 tw:items-center tw:rounded-lg tw:bg-white tw:px-2 tw:md:px-3"
										key={`${filteringKey}-${idx}`}
									>
										<img
											src={icon}
											alt={title}
											className="tw:h-[30px] tw:w-[30px] tw:md:h-[34px] tw:md:w-[34px] tw:xl:h-[40px] tw:xl:w-[40px]"
										/>
										<DesktopFilterSelect filteringControl={control} />
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>

			{/* Mobile View */}
			<div className="tw:fixed tw:right-0 tw:bottom-[15px] tw:left-0 tw:z-[1] tw:flex tw:justify-center tw:md:hidden">
				<div className="tw:rounded-lg tw:bg-[#0756C0] tw:px-4 tw:font-elza">
					<ExpandMobileFilters />
					<MobileFilters>
						{filteringControls.map((control, idx) => (
							<MobileFilterSelect
								key={`${control.filteringKey}-${idx}`}
								filteringControl={control}
							/>
						))}
					</MobileFilters>
				</div>
			</div>
		</>
	);
};

const DesktopFilterSelect = ({ filteringControl }) => {
	const { title, filteringKey, options } = filteringControl;
	const { selectedOption, handleChange } = useDesktopFilterSelect(filteringKey);

	return (
		<div className="tw:flex tw:w-full tw:flex-col">
			<label className="tw:pl-3 tw:text-sm tw:leading-4 tw:font-semibold tw:text-[#0756C0]">
				{title}
			</label>
			<select
				className="tw:ml-3 tw:w-full tw:max-w-90% tw:border-0 tw:pr-8 tw:pl-0 tw:text-sm tw:leading-4 tw:font-medium tw:text-[#212121] tw:md:max-w-full tw:xl:text-base"
				onChange={handleChange}
				value={selectedOption}
				id={`${filteringKey}_Input`}
				data-filtering-key={filteringKey}
			>
				{options.map((option, optionIdx) => (
					<option
						key={`${option.value}-${optionIdx}`}
						value={option.value}
						disabled={option.value === "default"}
					>
						{option.label}
					</option>
				))}
			</select>
		</div>
	);
};

DesktopFilterSelect.propTypes = {
	filteringControl: PropTypes.shape({
		title: PropTypes.string.isRequired,
		filteringKey: PropTypes.string.isRequired,
		options: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.string.isRequired,
				label: PropTypes.string.isRequired,
			}),
		).isRequired,
	}).isRequired,
};

const MobileFilterSelect = ({ filteringControl }) => {
	const { title, options } = filteringControl;
	const { isOpen, toggle, displayIndicator, handleChange, displayValue } =
		useMobileFilterSelect(filteringControl);

	return (
		<>
			<div
				className="tw:flex tw:h-[40px] tw:w-full tw:cursor-pointer tw:items-center tw:justify-between tw:rounded-[2px] tw:border tw:border-solid tw:border-[rgb(214,219,222)] tw:bg-white tw:px-[15px] tw:py-[13px] tw:text-[#333]"
				onClick={toggle}
				data-testid="select-container"
			>
				<div className="tw:relative tw:inline-flex tw:items-center">
					<p className="tw:my-[4px] tw:block tw:text-left tw:text-[14px] tw:font-semibold">
						{title}
					</p>
					{displayIndicator && (
						<span className="tw:ml-[15px] tw:inline-block tw:h-[7px] tw:w-[7px] tw:rounded-full tw:bg-[#1d8ce0]" />
					)}
				</div>
				<div className="tw:inline-flex tw:items-center">
					{displayValue && (
						<p
							role="button"
							aria-haspopup="true"
							className="tw:m-[4px_0] tw:block tw:text-left tw:text-[14px] tw:font-semibold"
						>
							{displayValue}
						</p>
					)}
				</div>
			</div>
			{isOpen && (
				<div
					id={`optionsContainer${title.split(" ").join("")}`}
					role="listbox"
					aria-label={title}
					aria-expanded="true"
					className="tw:fixed tw:bottom-0 tw:left-0 tw:z-1 tw:flex tw:w-full tw:flex-col tw:rounded-t-[10px] tw:bg-white tw:p-[25px] tw:transition-all"
				>
					<p className="tw:my-[4px] tw:block tw:text-center tw:font-elza tw:text-[15px] tw:leading-normal tw:font-semibold tw:text-[#333]">
						{title}
					</p>
					{options.map((option, idx) => (
						<button
							key={`${option.value}-${idx}`}
							role="option"
							value={option.value}
							hidden={option.value === "default"}
							onClick={handleChange}
							className="tw:m-[5px_5px_5px_0] tw:min-w-[75px] tw:rounded-[5px] tw:border-none tw:bg-[#e4e4e4] tw:p-[8px] tw:font-elza tw:text-[13px] tw:leading-[1.4375em] tw:font-semibold tw:text-[#111] tw:hover:bg-[#1d8ce0] tw:hover:text-white"
						>
							{option.label}
						</button>
					))}
				</div>
			)}
		</>
	);
};

MobileFilterSelect.propTypes = {
	filteringControl: PropTypes.shape({
		title: PropTypes.string.isRequired,
		filteringKey: PropTypes.string.isRequired,
		options: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.string.isRequired,
				label: PropTypes.string.isRequired,
			}),
		).isRequired,
	}).isRequired,
};

// Rate Compare styled components
const ExpandMobileFilters = () => {
	const { isOpen, toggle } = useFilter();
	if (isOpen) return null;

	const filterLabel = "Filters";

	return (
		<div
			role="button"
			aria-haspopup="true"
			aria-label="Filter Results"
			onClick={toggle}
			className="tw:flex tw:h-11 tw:cursor-pointer tw:flex-row tw:items-center"
		>
			<p className="tw:m-0 tw:flex tw:text-base tw:leading-4 tw:font-semibold tw:text-white">
				{filterLabel}
			</p>
		</div>
	);
};

const MobileFilters = ({ children }) => {
	const { isOpen, toggle, applyFilters, clearFilters } = useFilter();
	if (!isOpen) return null;

	return (
		<div className="tw:fixed tw:right-0 tw:bottom-0 tw:left-0 tw:isolate tw:flex tw:transition-all">
			{/* Gray overlay */}
			<div
				className="tw:fixed tw:top-0 tw:left-0 tw:z-1 tw:h-[calc(100vh-50px)] tw:w-full tw:bg-[gray]/30 tw:transition-all"
				onClick={toggle}
			/>

			{/* Filter container */}
			<div className="tw:z-2 tw:flex tw:w-full tw:flex-col tw:rounded-t-[10px] tw:bg-white tw:p-[10px]">
				<p className="tw:my-[10px] tw:inline-flex tw:items-center tw:justify-center tw:text-center tw:text-[16px] tw:font-semibold tw:text-[#111]">
					Filter Results
				</p>
				{children}
				<div className="tw:flex tw:justify-around tw:py-4">
					<button
						type="button"
						onClick={clearFilters}
						className="tw:rounded-lg tw:bg-white tw:px-8 tw:py-5 tw:text-base tw:leading-6 tw:font-semibold tw:text-[#0756C0]"
					>
						Clear All
					</button>
					<button
						type="button"
						onClick={applyFilters}
						className="tw:rounded-lg tw:bg-[#0756C0] tw:px-8 tw:py-5 tw:text-base tw:leading-6 tw:font-semibold tw:text-white"
					>
						Apply Filters
					</button>
				</div>
			</div>
		</div>
	);
};

MobileFilters.propTypes = {
	children: PropTypes.node.isRequired,
};
